/**
 * Profile section listing the user's active route alerts, with delete.
 * Renders nothing when the user has no alerts (keeps Profile uncluttered).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteSubscription, listSubscriptions } from '../lib/api';
import { useT } from '../lib/i18n';
import { hapticSelection, hapticError } from '../lib/telegram';
import { Card, CardTitle } from './Card';

export function RouteAlertsCard({ nameKey }: { nameKey: 'nameEn' | 'nameSr' }): JSX.Element | null {
  const { t } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['subscriptions'], queryFn: listSubscriptions });
  const del = useMutation({
    mutationFn: (id: string) => deleteSubscription(id),
    onMutate: async (id) => {
      // Optimistic: drop the alert from the list immediately so the ✕ feels instant.
      await qc.cancelQueries({ queryKey: ['subscriptions'] });
      const prev = qc.getQueryData<typeof q.data>(['subscriptions']);
      if (prev) {
        qc.setQueryData(['subscriptions'], {
          ...prev,
          items: prev.items.filter((s) => s.id !== id),
        });
      }
      return { prev };
    },
    onError: (_e: Error, _id, ctx) => {
      hapticError();
      if (ctx?.prev) qc.setQueryData(['subscriptions'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['subscriptions'] }),
  });

  const items = q.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <Card className="space-y-2">
      <CardTitle>🔔 {t('profile.alerts.title')}</CardTitle>
      {items.map((s) => (
        <div
          key={s.id}
          className="flex items-center justify-between gap-2 rounded-input bg-tg-bg-secondary p-2.5"
        >
          <span className="min-w-0 truncate text-sm text-tg-text">
            {s.origin[nameKey]} → {s.destination[nameKey]}
            {s.maxPrice != null && (
              <span className="text-tg-hint"> · ≤ {Math.round(s.maxPrice)}</span>
            )}
          </span>
          <button
            type="button"
            onClick={() => {
              hapticSelection();
              del.mutate(s.id);
            }}
            aria-label={t('alert.delete')}
            className="flex-none px-2 text-sm font-medium text-destructive"
          >
            ✕
          </button>
        </div>
      ))}
    </Card>
  );
}
